import { AxiosRequestConfig } from 'axios';
import instance from 'api';
import { IRoomModel } from 'interfaces';
import { DefectAct } from './interfaces';

export const getDefectAct = (applicationNumber: string) => {
  return instance()
    .get<DefectAct.IDefectAct>('/defectAct/v2/loadByApplicationNumber', { params: { applicationNumber } })
    .then((response) => response.data);
};

export const sendTmcToSupplier = (applicationNumber: string) => {
  return instance()
    .post<void>(`/defectAct/tmc/${applicationNumber}/sendToSupplier`)
    .then((response) => response.data);
};

export const completeTmcPurchase = (applicationNumber: string) => {
  return instance()
    .post<void>(`/defectAct/tmc/${applicationNumber}/purchase/complete`)
    .then((response) => response.data);
};

export const getTmcWriteOffMaterials = (applicationNumber: string) => {
  return instance()
    .get<DefectAct.ITmcWriteOffMaterial[]>(`/defectAct/tmc/${applicationNumber}/writeOff/materials`)
    .then((response) => response.data);
};

export const validateTmcWriteOff = (applicationNumber: string, request: DefectAct.ITmcWriteOffRequest) => {
  return instance()
    .post<DefectAct.ITmcWriteOffValidation>(`/defectAct/tmc/${applicationNumber}/writeOff/validate`, request)
    .then((response) => response.data);
};

export const sendTmcWriteOff = (
  applicationNumber: string,
  request: DefectAct.ITmcWriteOffRequest,
  files: File[],
) => {
  const formData = new FormData();
  formData.append('request', new Blob([JSON.stringify(request)], { type: 'application/json' }));
  files.forEach((file) => formData.append('files', file));

  return instance()
    .post<void>(`/defectAct/tmc/${applicationNumber}/writeOff`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((response) => response.data);
};

export const getTmcWriteOffHistory = (applicationNumber: string) => {
  return instance()
    .get<DefectAct.ITmcWriteOffHistory[]>(`/defectAct/tmc/${applicationNumber}/writeOff/history`)
    .then((response) => response.data);
};

export const updateDefectAct = (applicationNumber: string, defectAct: DefectAct.IDefectAct) => {
  return instance()
    .post<DefectAct.IDefectAct>('/defectAct/v3/updateDefAct', { ...defectAct, applicationNumber })
    .then((response) => response.data);
};

// Pre-DA notifications (новый flow, version=2)
export const listPreDaNotifications = (applicationNumber: string) => {
  return instance()
    .get<DefectAct.IPreNotificationsList>('/defectAct/v2/listPreDaNotifications', {
      params: { applicationNumber },
    })
    .then((response) => response.data);
};

export const insertPreDaNotification = (payload: DefectAct.IPreNotificationInsert) => {
  return instance()
    .post<DefectAct.IPreNotification>('/defectAct/v2/insertPreDaNotification', payload)
    .then((response) => response.data);
};

export const insertBuilderPreDaNotification = (payload: DefectAct.IPreNotificationBuilderInsert) => {
  return instance()
    .post<DefectAct.IPreNotification>('/defectAct/v2/insertBuilderPreDaNotification', payload)
    .then((response) => response.data);
};

export const updatePreDaNotification = (payload: DefectAct.IPreNotificationInsert) => {
  return instance()
    .post<DefectAct.IPreNotification>('/defectAct/v2/updatePreDaNotification', payload)
    .then((response) => response.data);
};

export const updateBuilderPreDaNotification = (payload: DefectAct.IPreNotificationInsert) => {
  return instance()
    .post<DefectAct.IPreNotification>('/defectAct/v2/updateBuilderPreDaNotification', payload)
    .then((response) => response.data);
};

export const sendPreDaNotification = (payload: DefectAct.IPreNotificationSend) => {
  return instance()
    .post<DefectAct.IPreNotification>('/defectAct/v2/sendPreDaNotification', payload)
    .then((response) => response.data);
};

export const convertAndSendPreNotifications = (applicationNumber: string, contragentGuid: string) => {
  return instance()
    .post<void>('/defectAct/v2/convertAndSendPreNotifications', { applicationNumber, contragentGuid })
    .then((response) => response.data);
};

export const closeDefectAct = (applicationNumber: string) => {
  return instance()
    .post('/defectAct/closeNoCost', { applicationNumber })
    .then((response) => response.data);
};

export const changeIsAgreedDefectAct = (applicationNumber: string) => {
  return instance()
    .post('/defectAct/changeIsAgreed', { applicationNumber })
    .then((response) => response.data);
};

export const completeDefectAct = (appNumber: string, addToMailing: boolean, responsibility: string) => {
  return instance()
    .post('/defectAct/completeByTechDepConsideration', { appNumber, addToMailing, responsibility })
    .then((response) => response.data);
};

export const rejectDefectAct = (appNumber: string) => {
  return instance()
    .post('/defectAct/rejectByTechDepConsideration', { appNumber })
    .then((response) => response.data);
};

export const getSignerCompanies = (applicationNumber: string) => {
  return instance()
    .get<DefectAct.ISignerCompany[]>('/defectAct/v2/getSignerCompanies', {
      params: { applicationNumber },
    })
    .then((response) => response.data);
};

export const saveDefectActMembers = (applicationNumber: string, members: DefectAct.IMember[]) => {
  return instance()
    .post<void>('/defectAct/v2/saveDefectActMembers', { applicationNumber, members })
    .then((response) => response.data);
};

export const getEmployeesFromPartners = (config?: AxiosRequestConfig) => {
  return instance()
    .get<DefectAct.IEmployeeFromPartner[]>('/defectAct/v2/getEmployeesFromPartners', config)
    .then((response) => response.data);
};

export const loadDefectActFile = (applicationNumber: string) => {
  return instance().post<Blob>('/defectAct/defectActFileLoad', { applicationNumber }, { responseType: 'blob' });
};

export const assignToTechDep = (appNumber: string) => {
  return instance()
    .post<DefectAct.IDefectAct>('/defectAct/assignToTechDep', { appNumber })
    .then((response) => response.data);
};

export const insertDefectActDocument = (applicationNumber: string, document: DefectAct.IDocument) => {
  return instance()
    .post<DefectAct.IDocument[]>('/defectAct/insertDefActDocument', { ...document, applicationNumber })
    .then((response) => response.data);
};

export const deleteDefectActDocument = (applicationNumber: string, defectActDocumentId: number) => {
  return instance()
    .post('/defectAct/deleteDocument', { applicationNumber, defectActDocumentId })
    .then((response) => response.data);
};

// Dictionaries
export const getDefectActPositions = () => {
  return instance()
    .post<DefectAct.IPosition[]>('/dictionary/defActPositions', {})
    .then((response) => response.data);
};

export const getDefectActReasons = () => {
  return instance()
    .post<DefectAct.IReason[]>('/dictionary/defectReasons', {})
    .then((response) => response.data);
};

export const getDefectActWorkGuaranteeListAllNotDeleted = () => {
  return instance()
    .get<DefectAct.IWorkGuarantee[]>('/defectActWorkGuarantee/listAllNotDeleted')
    .then((response) => response.data);
};

export const getDefectActBrandListAllNotDeleted = () => {
  return instance()
    .get<DefectAct.IBrand[]>('/defectActBrand/listAllNotDeleted')
    .then((response) => response.data);
};

export const getRooms = (realEstateGuid: string) => {
  return instance()
    .post<IRoomModel[]>('/dictionary/rooms', { realEstateGuid })
    .then((response) => response.data);
};
